<template>
    <div class="mt-6 mb-11 flex justify-between items-center">
        <h2 class="font-bold text-4xl">
            {{preview ? 'Survey Preview' : surveyModel.name}}
        </h2>
        <jet-button v-if="preview"
                    @click.prevent="back"
                    :bg-dark="true"
        >
            Close Preview
        </jet-button>
    </div>

    <div class="p-6 shadow-md rounded-md mb-10">
        <h2 v-if="preview" class="font-bold text-3xl mb-4">
            {{ surveyModel.name }}
        </h2>
        <div class="ck-content mb-10 text-lg" v-html="surveyModel.intro_text"></div>

        <div class="mb-4">
            <div class="text-2xl leading-6 flex justify-between items-center">
                <h3 v-if="currentSection" class="font-bold">
                    Survey Questions
                    <template v-if="surveyModel.sections[currentSection - 1].name">
                        {{ '- ' + surveyModel.sections[currentSection - 1].name }}
                    </template>
                </h3>
                <h3 v-else class="font-bold">Profile Questions</h3>
                <p>Step {{ currentSection }} of {{ surveyModel.sections.length }}</p>
            </div>
            <p v-if="currentSection" class="text-lg leading-5 mt-2">
                {{ surveyModel.sections[currentSection - 1].description }}
            </p>
        </div>

        <!--<div class="border-b pb-4 mb-8">
            <p v-if="currentSection" class="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1 leading-tight">
                Section: {{ currentSection }} of {{ surveyModel.sections.length }}
            </p>

            <div class="mb-3 flex flex-col md:flex-row md:items-center md:justify-between">
                <h4 v-if="currentSection" class="text-xl font-bold text-gray-600 dark:text-gray-300">
                    Survey Questions
                    <template v-if="surveyModel.sections[currentSection - 1].name">
                        {{ '- ' + surveyModel.sections[currentSection - 1].name }}
                    </template>
                </h4>
                <h4 v-else class="text-xl font-bold text-gray-600 dark:text-gray-300">Profile Questions</h4>

                <div class="flex items-center md:w-64">
                    <div class="w-full bg-white dark:bg-gray-600 rounded-full mr-2 border">
                        <div class="rounded-full bg-gray-600 dark:bg-white text-xs leading-none h-2 text-center text-white"
                             :style="{width: currentProgress}"></div>
                    </div>
                    <div class="text-xs w-10 text-gray-600 dark:text-gray-200">{{ currentProgress }}</div>
                </div>
            </div>

            <p v-if="currentSection" class="text-gray-600 dark:text-gray-400">
                {{ surveyModel.sections[currentSection - 1].description }}
            </p>
        </div>-->

        <div v-if="!currentSection && surveyModel.profile_fields.length"
             class="profile-questions divide-y divide-dark"
        >
            <div v-for="(profileField, profileFieldIndex) in surveyModel.profile_fields"
                 :key="'profileField-' + profileFieldIndex"
                 :class="profileFieldIndex === 0 ? 'pb-5' : 'py-5'"
            >
                <div class="group-fields">
                    <h4 class="text-xl font-bold leading-6 mb-4">{{ profileField.name }}</h4>
                    <div class="grid gap-3 md:grid-cols-4">
                        <label v-for="(profileFieldValue, profileFieldValueIndex) in profileField.values"
                               :key="'profileFieldValue-' + profileFieldIndex + '-' + profileFieldValueIndex"
                               class="inline-flex items-center text-gray-600 dark:text-gray-400">
                            <input type="radio"
                                   v-model="profile[profileField.id].survey_profile_field_value_id"
                                   :value="profileFieldValue.id"
                                   @change="updateProfileField(profileField.id)"
                                   class="text-dark form-radio focus:border-darkh focus:outline-none focus:shadow-outline-white">
                            <span class="ml-2">{{ profileFieldValue.value }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="questions divide-y divide-dark">
            <div v-for="(question,questionIndex) in surveyModel.sections[currentSection - 1].questions"
                 :key="'question-' + (currentSection - 1) + '-' + questionIndex"
                 :class="questionIndex === 0 ? 'pb-5' : 'py-5'"
            >
                <div class="mb-4">
                    <h4 class="text-xl font-bold leading-6">
                        {{ questionIndex + 1 }}. {{ question.content }}
                    </h4>
                    <p v-if="question.description">
                        {{ question.description }}
                    </p>
                </div>
                <survey-answer-picker class="lg:w-1/2"
                                      :answers="question.answers"
                                      :preview="preview"
                                      v-model="form.userAnswers[question.id].answer"
                                      @change="saveResponse(question)"
                />
                <div v-if="question.allow_response_comment"
                     class="custom-answer lg:w-1/2 mt-6">
                    <label class="block text-sm">
                    <span v-if="question.response_comment_label"
                          class="block text-lg font-semibold leading-5 mb-2.5"
                    >
                        {{ question.response_comment_label }}
                    </span>
                        <jet-textarea @change="saveResponse(question)"
                                      v-model="form.userAnswers[question.id].comment"
                        />
                    </label>
                </div>
            </div>
        </div>
    </div>

    <div class="flex justify-between">
        <jet-button v-if="currentSection > 1 || (surveyModel.profile_fields.length && currentSection)"
                    @click.prevent="prevSection"
                    :bg-dark="true"
        >
            <svg class="mr-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path
                    d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
            </svg>
            Previous
        </jet-button>
        <jet-button v-if="currentSection < surveyModel.sections.length"
                    :disabled="currentSection === 0 && !profileFilled && !preview"
                    @click.prevent="nextSection"
                    class="ml-auto"
                    :bg-dark="true"
        >
            Next
            <svg class="ml-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path
                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
            </svg>
        </jet-button>

        <jet-button v-else
                    @click.prevent="finishSurvey"
                    class="ml-auto"
                    :bg-dark="true"
        >
            Finish
        </jet-button>
    </div>
</template>

<script>
import JetButton from "@/Jetstream/Button";
import JetInput from "@/Jetstream/Input";
import JetSelect from "@/Jetstream/Select";
import JetTextarea from "@/Jetstream/Textarea";
import {computed, inject, reactive, ref} from "vue";
import {Inertia} from "@inertiajs/inertia";
import SurveyAnswerPicker from "@/Components/Survey/SurveyAnswerPicker";

export default {
    name: "Survey",
    components: {
        SurveyAnswerPicker,
        JetButton,
        JetInput,
        JetSelect,
        JetTextarea,
    },
    props: {
        preview: {
            type: Boolean,
            default: false,
        },
        surveyModel: {
            type: Object,
            required: true,
        },
        surveyResponses: {
            type: Object,
            required: true,
        },
        respondentToken: {
            type: String,
            default: null
        },
        firstUnansweredSection: {
            type: Number,
            default: 1
        },
        respondentProfile: {
            type: Object,
            default: {}
        }
    },
    setup(props) {
        const $toast = inject('$toast');

        const currentProgress = computed(() => {
            return Math.round(currentSection.value / props.surveyModel.sections.length * 100) + '%';
        })

        const scrollToTop = () => {
            document.getElementById('mainContent').scrollTop = 0;
        }

        const sectionAnswered = (currentSection) => {
            if (!currentSection) {
                return true;
            }
            const answeredQuestions = Object.keys(props.surveyResponses);

            const sectionAnswered = props.surveyModel.sections[currentSection - 1].questions
                .map((question) => question.id?.toString())
                .every((questionId) => answeredQuestions.includes(questionId));

            if (sectionAnswered) {
                return true;
            }

            $toast.warning('Please answer all questions in the current section before proceeding.')
            return false;
        }

        const nextSection = () => {
            if (currentSection.value < props.surveyModel.sections.length
                && (props.preview || sectionAnswered(currentSection.value))) {
                currentSection.value += 1;
                scrollToTop();
            }
        }

        const prevSection = () => {
            if (currentSection.value > 1 || (props.surveyModel.profile_fields.length && currentSection.value)) {
                currentSection.value -= 1;
                scrollToTop();
            }
        }

        const form = reactive({
            userAnswers: {}
        })

        props.surveyModel.sections.forEach((section) => {
            section.questions.forEach((question) => {
                form.userAnswers[question.id] = {
                    answer: props.surveyResponses[question.id] ? props.surveyResponses[question.id].answer || null : null,
                    comment: props.surveyResponses[question.id] ? props.surveyResponses[question.id].comment || null : null,
                }
            })
        });

        const back = () => {
            window.history.back();
        }

        const saveResponse = (question) => {
            if (props.preview) return;
            Inertia.put(route('tenant.survey.fill.response', {
                tenant: props.surveyModel.tenant_id,
                respondentToken: props.respondentToken
            }), {
                question: {
                    id: question.id,
                    survey_section_id: question.survey_section_id,
                },
                answer: {
                    id: form.userAnswers[question.id].answer.id,
                    value: form.userAnswers[question.id].answer.value,
                },
                comment: form.userAnswers[question.id].comment || null,
            });
        }

        const profile = reactive({});
        props.surveyModel.profile_fields.map((profileField) => {
            profile[profileField.id] = ref(props.respondentProfile && profileField.id in props.respondentProfile
                ? props.respondentProfile[profileField.id]
                : {
                    survey_profile_field_id: profileField.id,
                    survey_profile_field_value_id: null,
                })
        });

        const profileFilled = computed(() => {
            for (const key in profile) {
                if (profile[key].survey_profile_field_value_id === null) return false;
            }

            return true;
        });

        const updateProfileField = (profileFieldId) => {
            if (props.preview) return;
            Inertia.put(route('tenant.survey.fill.profile', {
                tenant: props.surveyModel.tenant_id,
                respondentToken: props.respondentToken
            }), profile[profileFieldId]);
        }

        const currentSection = ref(0)

        if (Object.keys(props.surveyResponses).length) {
            //if we have answers, we go to the first unanswered question
            currentSection.value = props.firstUnansweredSection;
        } else if (!props.surveyModel.profile_fields.length || (!props.preview && profileFilled.value)) {
            //if we don't have answers, and we don't have profile fields, skip profile step
            currentSection.value = 1;
        }

        const finishSurvey = () => {
            if (props.preview) {
                $toast.success('Congratulations! You have finished the preview! This preview does not save any answers.');
                return;
            }
            Inertia.post(route('tenant.survey.fill.finish', {
                tenant: props.surveyModel.tenant_id,
                respondentToken: props.respondentToken
            }));
        }

        return {
            currentSection,
            currentProgress,
            nextSection,
            prevSection,
            form,
            back,
            saveResponse,
            profile,
            profileFilled,
            updateProfileField,
            finishSurvey,
        }
    }
}
</script>

<style lang="scss">
@import "../../sass/ck-content";

body {
    overflow: hidden;
}
</style>
